import React, { useEffect, useState } from 'react'
import { ToastContext } from '@pmi/pmi-iqos-ds-react'
import {
  cookie,
  constants,
  parseTranslations,
  PublishedComponent,
  isAuthenticated
} from '@pmi/pmi-iqos-shared-react'
import { getInitialPropsMock } from '@dcx-common/utils'
import SearchTranslations from './search.json'
import AutocompleteSearchComp from './index-autocomplete-search'
import InstantSearchComp from './index-instantsearch'
import { IndexNameTypes, IndexIdTypes, isAV, getSpiceIdHash } from './utils'
import { liteClient as algoliasearch } from 'algoliasearch/lite'

const SEARCH_TYPES = {
  autocomplete: 'autocomplete',
  instantsearch: 'instantsearch'
}

const searchClient = algoliasearch(constants.ALGOLIA_ID, constants.ALGOLIA_KEY)

const SearchComponent = props => {
  const { translations } = props
  const searchType = props.searchType || SEARCH_TYPES.instantsearch
  const [initialQuerySuggestionsHits, setInitialQuerySuggestionsHits] =
    useState([])
  const [spiceIdHash, setSpiceIdHash] = useState('')
  const [ready, setReady] = useState(false)

  const getVisibilityFilter = () => {
    if (!isAuthenticated()) {
      return ' AND visibleToGuests: true' // Only items visible to Guests
    } else if (!isAV()) {
      return ' AND visibleToASD: true' // Only items visible to ASD
    }
    return '' // All items visible to AV users
  }

  const sourcesConfiguration = [
    {
      text: translations.tk_productsHeader,
      indexName: IndexNameTypes.PRODUCTS,
      indexId: IndexIdTypes.PRODUCTS_DEVICES,
      filters: '(categoryID:1 OR categoryID:2)' + getVisibilityFilter(),
      limitAutoSearch: 12,
      limitSearchPage: 12
    },
    {
      text: translations.tk_consumablesHeader,
      indexName: IndexNameTypes.PRODUCTS,
      indexId: IndexIdTypes.PRODUCTS_CONSUMABLES,
      filters: 'categoryID:3' + getVisibilityFilter(),
      limitAutoSearch: 12,
      limitSearchPage: 12
    },
    {
      text: translations.tk_supportHeader,
      indexName: IndexNameTypes.ARTICLES,
      indexId: IndexIdTypes.ARTICLES_SUPPORT,
      filters: 'categoryID:support' + getVisibilityFilter(),
      limitAutoSearch: 4,
      limitSearchPage: 9
    },
    {
      text: translations.tk_faqsHeader,
      indexName: IndexNameTypes.ARTICLES,
      indexId: IndexIdTypes.ARTICLES_FAQ,
      filters: 'categoryID:faqs' + getVisibilityFilter(),
      limitAutoSearch: 4,
      limitSearchPage: 9
    },
    {
      text: translations.tk_newsHeader,
      indexName: IndexNameTypes.ARTICLES,
      indexId: IndexIdTypes.ARTICLES_NEWS,
      filters: 'categoryID:news' + getVisibilityFilter(),
      limitAutoSearch: 2,
      limitSearchPage: 9
    }
  ]

  const sourceQuerySuggestionsConfiguration = {
    text: translations.tk_recommendedSearchHeader,
    indexName: IndexNameTypes.QUERY_SUGGESTIONS,
    limitAutoSearch: 4
  }

  if (searchType === SEARCH_TYPES.autocomplete) {
    sourcesConfiguration.unshift(sourceQuerySuggestionsConfiguration)
  }

  useEffect(() => {
    // Fetch the initial QUERY_SUGGESTIONS to be displayed before start search or in case of no results
    const fetchQuerySuggestions = async () => {
      const res = await searchClient.search({
        requests: [
          {
            indexName: sourceQuerySuggestionsConfiguration.indexName,
            query: '',
            hitsPerPage: sourceQuerySuggestionsConfiguration.limitAutoSearch
          }
        ]
      })
      if (res?.results[0]?.hits) {
        res.results[0].hits.forEach((h, i) => (h.__position = i))
        setInitialQuerySuggestionsHits(res?.results[0]?.hits)
      }
    }
    fetchQuerySuggestions()

    const fetchSpiceIdHash = async () => {
      const spiceIdHash = await getSpiceIdHash(cookie.getSpiceId())
      setSpiceIdHash(spiceIdHash)
      setReady(true)
    }
    fetchSpiceIdHash()
  }, [])

  const finalProps = {
    searchClient,
    sourcesConfiguration,
    initialQuerySuggestionsHits,
    spiceIdHash,
    ...props
  }

  if (!ready) return null

  // Render Autocomplete search
  if (searchType === SEARCH_TYPES.autocomplete) {
    return <AutocompleteSearchComp {...finalProps} />
  }
  // Render InstantSearch search
  return <InstantSearchComp {...finalProps} />
}

const Search = props => {
  const searchType = props.searchType || SEARCH_TYPES.instantsearch

  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={SearchComponent}
        fullWidth={searchType === SEARCH_TYPES.instantsearch}
        mainLayout={searchType === SEARCH_TYPES.autocomplete}
        {...ComponentDefaultProps}
        {...props}
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...SearchTranslations
}

const ComponentDefaultProps = {
  componentName: 'Search',
  translations: {
    ...parseTranslations(ComponentTranslations, true)
  }
}

Search.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    includeCommonTranslations: false,
    getSpecificInitialProps: () => {
      const searchType = req.query?.searchType || SEARCH_TYPES.instantsearch // autocomplete || instantsearch
      const recommendedContent =
        req.query?.mock === 'true'
          ? getInitialPropsMock('getSearchRecommendedContent')
          : req.body?.extra?.recommendedContent

      return {
        searchType,
        recommendedContent
      }
    }
  })
}

export default Search
